import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, IconButton, Alert, CircularProgress, Typography, Box, TextField, Button } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QRCode from "react-qr-code";
import { apiHelper, baseUrl } from "helpers/util";
import folkLogo from "../../images/folklogo.png";
import ColumnBox from "components/ColumnBox";

export default function LateAttendance() {
    const [mobileNo, setMobileNo] = useState("");
    const [loading, setLoading] = useState(false);
    const [record, setRecord] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const pollIntervalRef = useRef(null);

    // Payment UI State
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState("idle"); // idle | waiting | success
    const [qrUrl, setQrUrl] = useState("");

    const isValidMobile = (val) => /^\d{10}$/.test(String(val || "").trim());

    // Clean up when unmounting
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            setQrModalOpen(false);
            setPaymentStatus("idle");
        };
    }, []);

    const searchStudent = async (numberToSearch) => {
        const queryMobile = numberToSearch || mobileNo;
        if (!isValidMobile(queryMobile)) {
            setErrorMsg("Please enter a valid 10-digit mobile number.");
            return;
        }

        setLoading(true);
        setErrorMsg("");
        setRecord(null);
        setPaymentStatus("idle");

        try {
            const response = await apiHelper.get({
                url: `${baseUrl}/api/late_attendance_check_status`,
                params: { mobileNo: queryMobile.trim() }
            });

            // Handle case where backend didn't set Content-Type so it's a string
            const responseData = typeof response.json === 'string' ? JSON.parse(response.json) : response.json;

            if (response.status && responseData && responseData.length > 0) {
                // Get the first matching record
                const found = responseData[0];
                setRecord({
                    id: found["Record ID"] || found.id,
                    name: Array.isArray(found["Name"]) ? found["Name"][0] : (found["Name"] || "Unknown"),
                    mobileNo: Array.isArray(found["Mobile No."]) ? found["Mobile No."][0] : (found["Mobile No."] || found["Mobile No"] || queryMobile.trim()),
                    folkLevel: Array.isArray(found["FOLK Level (Display)"]) ? found["FOLK Level (Display)"][0] : (found["FOLK Level (Display)"] || found["FOLK Level"] || "Not assigned"),
                    folkGuide: Array.isArray(found["Guide Name"]) ? found["Guide Name"][0] : (found["Guide Name"] || "Not assigned"),
                    email: Array.isArray(found["Mail ID"]) ? found["Mail ID"][0] : (found["Mail ID"] || "Unknown"),
                    attendanceTime: found["Attendance Time"] ? new Date(found["Attendance Time"]).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : "N/A"
                });
            } else {
                setErrorMsg("No attendance record found for this mobile number.");
            }
        } catch (err) {
            console.error("Search error:", err);
            setErrorMsg("An error occurred while searching. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleShowQr = async () => {
        setQrModalOpen(true);
        setPaymentStatus("waiting");

        try {
            const response = await apiHelper.post({
                url: `${baseUrl}/api/late_attendance_create_qr`,
                data: {
                    mobileNo: record.mobileNo,
                    name: record.name,
                    email: record.email,
                    folkGuide: record.folkGuide
                }
            });

            const responseData = typeof response.json === 'string' ? JSON.parse(response.json) : response.json;

            if (response.status && responseData && (responseData.qr_string || responseData.qr_image_url)) {
                setQrUrl(responseData.qr_string || responseData.qr_image_url);

                if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

                // Start polling for payment success every 5 seconds
                pollIntervalRef.current = setInterval(async () => {
                    try {
                        const statusRes = await apiHelper.get({
                            url: `${baseUrl}/api/late_attendance_check_status`,
                            params: { mobileNo: record.mobileNo }
                        });

                        const statusData = typeof statusRes.json === 'string' ? JSON.parse(statusRes.json) : statusRes.json;

                        if (statusRes.status && statusData && statusData.length > 0 && statusData[0].status === "Paid") {
                            clearInterval(pollIntervalRef.current);
                            await markPaymentSuccess(statusData[0].txnId || "");
                        }
                    } catch (err) {
                        console.error("Polling error", err);
                    }
                }, 5000);

            } else {
                console.error("No QR URL returned:", response);
                setPaymentStatus("idle");
                setErrorMsg("Failed to load Razorpay QR Code.");
                setQrModalOpen(false);
            }
        } catch (e) {
            console.error("Error creating QR:", e);
            setPaymentStatus("idle");
            setErrorMsg("Failed to generate payment QR. Is Razorpay configured?");
            setQrModalOpen(false);
        }
    };

    const markPaymentSuccess = async (txnId) => {
        setPaymentStatus("success");
        try {
            await apiHelper.post({
                url: `${baseUrl}/api/late_attendance_payment`,
                data: {
                    name: record.name,
                    mobileNo: record.mobileNo,
                    folkGuide: record.folkGuide,
                    folkLevel: record.folkLevel,
                    amount: 50,
                    paymentStatus: "Paid",
                    transactionId: txnId
                }
            });
        } catch (e) {
            console.error("Error logging payment to backend", e);
        }
    };

    const closeSuccess = () => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (window.currentPollInterval) clearInterval(window.currentPollInterval);
        setQrModalOpen(false);
        setPaymentStatus("idle");
        setRecord(null);
        setMobileNo("");
    };

    const handleCloseModal = () => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (window.currentPollInterval) clearInterval(window.currentPollInterval);
        setQrModalOpen(false);
    };

    return (
        <ColumnBox>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <img src={folkLogo} alt="FOLK Logo" style={{ width: '80px', marginBottom: '10px' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>Late Attendance</Typography>
            </Box>

            <Box sx={{ width: { xs: 'calc(100% - 32px)', sm: '400px' }, mx: 'auto', p: { xs: 2, sm: 3 }, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 3, bgcolor: '#ffffff' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#34495e' }}>
                    Mobile Number
                </Typography>

                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="98XXXXXXXX"
                        value={mobileNo}
                        onChange={(e) => {
                            const val = e.target.value;
                            setMobileNo(val);
                            if (val.length === 10) {
                                searchStudent(val);
                            }
                        }}
                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                        disabled={loading}
                        sx={{ bgcolor: '#f8fafc' }}
                        InputProps={{
                            endAdornment: loading ? <CircularProgress size={20} /> : null
                        }}
                    />
                </Box>

                {errorMsg && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{errorMsg}</Alert>}

                {record && (
                    <Box sx={{ mt: 3, p: 2.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Name:</Typography>
                            <Typography variant="body1" fontWeight="600">{record.name}</Typography>
                        </Box>

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">FOLK Guide:</Typography>
                            <Typography variant="body1" fontWeight="600">{record.folkGuide}</Typography>
                        </Box>

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">FOLK Level:</Typography>
                            <Typography variant="body1" fontWeight="600">{record.folkLevel}</Typography>
                        </Box>

                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Attendance Time:</Typography>
                            <Typography variant="body1" fontWeight="600" color="primary.main">{record.attendanceTime}</Typography>
                        </Box>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Button
                                variant="contained"
                                color="warning"
                                fullWidth
                                size="large"
                                onClick={handleShowQr}
                                sx={{ py: 1.5, fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 2, textTransform: 'none' }}
                            >
                                Show QR
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* QR Payment Modal */}
            <Dialog
                open={qrModalOpen}
                onClose={paymentStatus === "success" ? closeSuccess : handleCloseModal}
                PaperProps={{
                    style: {
                        backgroundColor: '#f8faff',
                        backgroundImage: 'linear-gradient(165deg, transparent 45%, #0066ff 45%, #0066ff 75%, transparent 75%)',
                        borderRadius: '24px',
                        padding: '16px',
                        width: '100%',
                        maxWidth: '380px',
                        overflow: 'hidden',
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
                    }
                }}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                }}
            >
                {paymentStatus !== "success" && (
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', left: 8, top: 8, color: '#ffffff', zIndex: 10 }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}

                <DialogContent sx={{ textAlign: 'center', p: 3, overflow: 'hidden' }}>
                    {paymentStatus === "waiting" ? (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
                                Sunday Donation ₹50.00
                            </Typography>
                            {qrUrl ? (
                                <Box sx={{ p: 2, bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 2 }}>
                                    {qrUrl.startsWith("http") ? (
                                        <img src={qrUrl} alt="Payment QR" style={{ height: "auto", maxWidth: "100%", width: "100%" }} />
                                    ) : (
                                        <QRCode 
                                            value={qrUrl} 
                                            size={250} 
                                            style={{ height: "auto", maxWidth: "100%", width: "100%" }} 
                                        />
                                    )}
                                </Box>
                            ) : (
                                <CircularProgress sx={{ my: 10 }} />
                            )}
                        </Box>
                    ) : paymentStatus === "success" ? (
                        <Box sx={{ py: 3 }}>
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main', mb: 2 }}>
                                ✓ Payment Received
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1, fontWeight: '500' }}>
                                Thank you for your donation!
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                You may now collect your<br />Prasadam Coupon.
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                size="large"
                                onClick={closeSuccess}
                                sx={{ py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
                            >
                                OK
                            </Button>
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </ColumnBox>
    );
}
